/* Icon picker: pack > group > icon. One group open at a time; the chosen icon always shows at the top. */
import { esc } from "../domain/format";
import { ICON_PACKS, findIcon, iconRef } from "../domain/icon-packs";
import { ic } from "./icons";

export interface IconPicker {
  html: string;
  /** wires the picker inside the sheet; call once after the sheet is open */
  mount(root: HTMLElement): void;
  /** selects an icon from outside (e.g. link ↔ memo swaps the default icon) */
  select(ref: string): void;
  /** redraws every icon (e.g. after the action colour changed) */
  redraw(): void;
  /** opens one group of the first pack (e.g. the group that matches the chosen category type); the selection stays */
  openGroup(groupId: string): void;
}

/**
 * @param selected icon ref saved on the record (or the default of a new one)
 * @param draw draws one icon: line icon for workspaces and categories, glass icon in the action colour for actions
 * @param openAtSelected editing an existing record: open the pack and group of its icon
 */
export function iconPicker(selected: string, draw: (ref: string) => string, onPick: (ref: string) => void, glassy: boolean, openAtSelected: boolean, initialGroup?: string): IconPicker {
  let sel = selected;
  let root: HTMLElement | null = null;
  const hit = findIcon(sel);
  const packOpen = new Set<string>([hit?.pack.packId ?? ICON_PACKS[0].packId]);
  let groupOpen: string | null = openAtSelected && hit ? `${hit.pack.packId}:${hit.group.groupId}`
    : initialGroup ? `${ICON_PACKS[0].packId}:${initialGroup}` : null;
  if (groupOpen) packOpen.add(groupOpen.split(":")[0]);

  const current = () => {
    const h = findIcon(sel);
    return `<span class="ipk-cur-ic">${draw(sel)}</span><span class="ipk-cur-t"><small>ไอคอนที่เลือก</small><b>${esc(h?.icon.name ?? sel)}</b>`
      + `<small>${h ? `${esc(h.pack.name)} · ${esc(h.group.name)}` : ""}</small></span>`;
  };
  const html = `<div class="ipk" id="ed-icons"><div class="ipk-cur" id="ipk-cur" aria-live="polite">${current()}</div>`
    + ICON_PACKS.map(p => {
      const n = p.groups.reduce((m, g) => m + g.icons.length, 0);
      const pOpen = packOpen.has(p.packId);
      return `<div class="ipk-pack" data-pack="${p.packId}">`
        + `<button type="button" class="ipk-phead" data-ipk-pack="${p.packId}" aria-expanded="${pOpen}" aria-controls="ipk-p-${p.packId}">`
        + `<span class="ipk-pname">${esc(p.name)}</span>${p.tier === "free" ? `<span class="ipk-badge">ฟรี</span>` : ""}<span class="ipk-n">${n} ไอคอน</span><span class="ipk-chev">${ic("down")}</span></button>`
        + `<div class="ipk-groups" id="ipk-p-${p.packId}" ${pOpen ? "" : "hidden"}>`
        + p.groups.map(g => {
          const key = `${p.packId}:${g.groupId}`, open = groupOpen === key;
          return `<div class="ipk-group" data-group="${g.groupId}">`
            + `<button type="button" class="ipk-ghead" data-ipk-group="${key}" aria-expanded="${open}" aria-controls="ipk-g-${p.packId}-${g.groupId}">`
            + `<span class="ipk-gname">${esc(g.name)}</span><span class="ipk-dot" ${hit && hit.pack === p && hit.group === g ? "" : "hidden"}></span><span class="ipk-n">${g.icons.length}</span><span class="ipk-chev">${ic("down")}</span></button>`
            + `<div class="icons ipk-grid ${glassy ? "glassy" : ""}" id="ipk-g-${p.packId}-${g.groupId}" ${open ? "" : "hidden"}>`
            + g.icons.map(i => { const ref = iconRef(p.packId, i.iconId);
              return `<button type="button" data-i="${ref}" aria-pressed="${ref === sel}" aria-label="${esc(i.name)}" title="${esc(i.name)}">${draw(ref)}</button>`; }).join("")
            + `</div></div>`;
        }).join("")
        + `</div></div>`;
    }).join("") + `</div>`;

  const sync = () => {
    if (!root) return;
    root.querySelector("#ipk-cur")!.innerHTML = current();
    root.querySelectorAll<HTMLElement>("[data-i]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.i === sel)));
    const h = findIcon(sel);
    root.querySelectorAll<HTMLElement>(".ipk-group").forEach(g => {
      const pack = g.closest<HTMLElement>(".ipk-pack")!.dataset.pack;
      g.querySelector<HTMLElement>(".ipk-dot")!.hidden = !(h && h.pack.packId === pack && h.group.groupId === g.dataset.group);
    });
  };
  const setGroup = (key: string | null) => {
    groupOpen = key;
    root!.querySelectorAll<HTMLElement>("[data-ipk-group]").forEach(b => {
      const open = b.dataset.ipkGroup === key;
      b.setAttribute("aria-expanded", String(open));
      root!.querySelector<HTMLElement>("#" + b.getAttribute("aria-controls"))!.hidden = !open;
    });
  };

  return {
    html,
    mount(r) {
      root = r.querySelector<HTMLElement>("#ed-icons");
      root!.addEventListener("click", e => {
        const t = e.target as Element;
        const pk = t.closest<HTMLElement>("[data-ipk-pack]");
        if (pk) {
          const id = pk.dataset.ipkPack!, open = !packOpen.has(id);
          if (open) packOpen.add(id); else packOpen.delete(id);
          pk.setAttribute("aria-expanded", String(open));
          root!.querySelector<HTMLElement>(`#ipk-p-${id}`)!.hidden = !open;
          return;
        }
        const gr = t.closest<HTMLElement>("[data-ipk-group]");
        if (gr) { setGroup(groupOpen === gr.dataset.ipkGroup ? null : gr.dataset.ipkGroup!); return; }
        const b = t.closest<HTMLElement>("[data-i]");
        if (b) { sel = b.dataset.i!; sync(); onPick(sel); }
      });
    },
    select(ref) { sel = ref; sync(); },
    openGroup(groupId) {
      const pack = ICON_PACKS[0];
      if (!root || !pack.groups.some(g => g.groupId === groupId)) return;
      if (!packOpen.has(pack.packId)) {
        packOpen.add(pack.packId);
        root.querySelector<HTMLElement>(`[data-ipk-pack="${pack.packId}"]`)!.setAttribute("aria-expanded", "true");
        root.querySelector<HTMLElement>(`#ipk-p-${pack.packId}`)!.hidden = false;
      }
      setGroup(`${pack.packId}:${groupId}`);
    },
    redraw() {
      if (!root) return;
      root.querySelectorAll<HTMLElement>("[data-i]").forEach(b => { b.innerHTML = draw(b.dataset.i!); });
      root.querySelector(".ipk-cur-ic")!.innerHTML = draw(sel);
    }
  };
}
